import type { WorkQueueCase } from '../../services/workQueue';
import { formatDateOnly } from '../../utils/dates';

// A small pill rendered next to the patient name in the work-queue list.
export interface CaseTag {
    key: string;
    label: string;
    tooltip: string;
    className: string; // pill bg/text/ring classes
    dotClassName: string; // leading dot color
}

// Phases where the T3P tag is not shown (pre-treatment).
const T3P_HIDDEN_PHASES: ReadonlySet<string> = new Set(['Design', 'Approval', 'Redesign']);

// Phases eligible for the Tx duration tag — active/ongoing treatment only.
const TX_DURATION_PHASES: ReadonlySet<string> = new Set([
    'Treatment',
    'Refinement',
    'Hold',
    'Retention'
]);

const TX_DURATION_RED_MONTHS = 24;
const TX_DURATION_AMBER_MONTHS = 20;
const TX_DURATION_YELLOW_MONTHS = 18;

// Calendar-month diff in UTC (approvedAt is a date-only value at UTC midnight).
function monthsSinceUtc(fromIso: string, now: Date): number {
    const from = new Date(fromIso);
    if (Number.isNaN(from.getTime())) return 0;
    let months =
        (now.getUTCFullYear() - from.getUTCFullYear()) * 12 +
        (now.getUTCMonth() - from.getUTCMonth());
    if (now.getUTCDate() < from.getUTCDate()) months -= 1;
    return Math.max(0, months);
}

function computeTxDurationTag(c: WorkQueueCase): CaseTag | null {
    if (!c.approvedAt || !TX_DURATION_PHASES.has(c.phase)) return null;
    const months = monthsSinceUtc(c.approvedAt, new Date());
    const tooltip = `In treatment ${months} month${months === 1 ? '' : 's'} (approved ${formatDateOnly(c.approvedAt)})`;
    if (months >= TX_DURATION_RED_MONTHS) {
        return {
            key: 'tx-duration',
            label: '24mo+',
            tooltip,
            className: 'bg-red-50 text-red-700 ring-red-600/20',
            dotClassName: 'bg-red-500'
        };
    }
    if (months >= TX_DURATION_AMBER_MONTHS) {
        return {
            key: 'tx-duration',
            label: '20mo+',
            tooltip,
            className: 'bg-amber-50 text-amber-700 ring-amber-200',
            dotClassName: 'bg-amber-500'
        };
    }
    if (months >= TX_DURATION_YELLOW_MONTHS) {
        return {
            key: 'tx-duration',
            label: '18mo+',
            tooltip,
            className: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20',
            dotClassName: 'bg-yellow-500'
        };
    }
    return null;
}

// Single source of truth for the badges shown per case row: Tx duration
// (treatment-phase cases only), version (always), and T3P (starter aligners
// during the first/conversion cycle). Priority has its own pill
// (components/work-queue/priority/) rendered ahead of these. Add future row
// badges here so every surface stays consistent.
export function computeCaseTags(c: WorkQueueCase): CaseTag[] {
    const tags: CaseTag[] = [];

    const durationTag = computeTxDurationTag(c);
    if (durationTag) tags.push(durationTag);

    // Version — always shown; falls back to v1 when unset (product decision).
    const isV2 = (c.version ?? '').trim().toUpperCase() === 'V2';
    tags.push({
        key: 'version',
        label: isV2 ? 'v2' : 'v1',
        tooltip: `Patient flow version: ${isV2 ? 'V2' : 'V1'}`,
        className: isV2
            ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
            : 'bg-gray-50 text-gray-600 ring-gray-500/20',
        dotClassName: isV2 ? 'bg-emerald-500' : 'bg-gray-400'
    });

    // T3P — starter aligners, only while the case is in its first (conversion)
    // cycle (no real refinement yet) and past the pre-treatment phases.
    if (
        c.alignerOption === 'Starter' &&
        (c.refinementsCount ?? 0) === 0 &&
        !T3P_HIDDEN_PHASES.has(c.phase)
    ) {
        tags.push({
            key: 't3p',
            label: 'T3P',
            tooltip:
                'Starter aligners (T3P): the first cycle runs through refinement to capture the post-bond scan',
            className: 'bg-violet-50 text-violet-700 ring-violet-600/20',
            dotClassName: 'bg-violet-500'
        });
    }

    return tags;
}
